import {
  action,
  computed,
  makeObservable,
  observable,
  ObservableMap,
  runInAction,
} from 'mobx';
import RootStore from './RootStore';
import { FaqApi, FaqMessageEntity } from '../api/faq.api';
import moment from 'moment';
import { IAutoMessage } from '../interfaces/faq.interface';
import { getInquiryCategoryName } from '../utils/inquiry';
import { InquiryCategoryEnum } from '../interfaces/filters.interface';
import { IRecipient } from '../interfaces/user.interface';

export type InputSelection = {
  inquiryCategory: number;
  inquirySubCategory: number;
  feedbackId: number;
  moreFaqId: number;
};

export enum ResponseFromUser {
  YES = 1,
  NO = 2,
  RESET_CATEGORY_SELECTION = -1,
  VIEW_MORE = -10,
  FAQ_OPTION_SEND_MESSAGE = -100,
}

export const TEMPORARY_INVALID_STRING: string = '-';
export const TEMPORARY_INVALID_NUMBER: number = -1;

export default class FaqStore {
  private rootStore: RootStore;

  @observable messages: ObservableMap<string, IAutoMessage> = observable.map();

  @observable private _inputSelectionMap = {} as InputSelection;

  private faq = {} as FaqMessageEntity;

  @action initializeFaqForNewInquiry = async (
    serviceId: number,
    serviceAttribute: number,
    partnerId: string,
  ) => {
    await this.fetchFaqDataForNewInquiry(
      serviceId,
      serviceAttribute,
      partnerId,
    );
    this.generateFirstFaqMessage();
  };

  private fetchFaqDataForNewInquiry = async (
    serviceId: number,
    serviceAttribute: number,
    partnerId: string,
  ) => {
    try {
      this.faq = await FaqApi.getFaqs(serviceId, serviceAttribute, partnerId);
    } catch (e: any) {
      // set temporary FaqMessageEntity. inquiry category is always Other 'その他' with no actual FAQ
      this.faq = {
        hotelId: TEMPORARY_INVALID_NUMBER,
        hotelName: TEMPORARY_INVALID_STRING,
        inquiryCategories: [
          {
            inquiryCategoryId: InquiryCategoryEnum.Other,
            inquiryCategory: getInquiryCategoryName(InquiryCategoryEnum.Other)!,
            inquirySubCategories: [
              {
                inquirySubCategoryId: TEMPORARY_INVALID_NUMBER,
                inquirySubCategory: TEMPORARY_INVALID_STRING,
                answer: TEMPORARY_INVALID_STRING,
              },
            ],
          },
        ],
      };
    }

    runInAction(() => {
      this.messages = observable.map(); // initialize messages
    });
    setTimeout(() => {
      this.rootStore.inquiryPageStore.lastMessageDivScrollIntoView();
    }, 50);
  };

  private generateFirstFaqMessage = () => {
    if (this.faq.hotelId === TEMPORARY_INVALID_NUMBER) {
      runInAction(() => {
        this.generateAndAddMessage({
          messageBody:
            'お問い合わせ内容を下のテキストボックスにご入力ください。\n宿泊施設にて確認の上ご案内いたしますまで、お時間いただくことご了承ください。\nお急ぎの場合は、詳細情報ページに記載の電話番号までお問い合わせください。',
        });
      });
      // set ids to create state so that isFaqUnresolved() returns true.
      this._inputSelectionMap.inquiryCategory =
        this.faq.inquiryCategories[0].inquiryCategoryId;
      this._inputSelectionMap.inquirySubCategory =
        this.faq.inquiryCategories[0].inquirySubCategories[0].inquirySubCategoryId;
      this._inputSelectionMap.feedbackId = ResponseFromUser.NO;
    } else {
      runInAction(() => {
        this.generateAndAddMessage({
          messageBody:
            'いつもありがとうございます。お問い合わせのカテゴリーを下記より選択してください。',
          option: this.faq.inquiryCategories
            .map((e) => ({
              id: e.inquiryCategoryId,
              value: e.inquiryCategory,
            }))
            .slice(0, 4)
            .concat({
              id: ResponseFromUser.VIEW_MORE,
              value: 'もっと見る',
            }),
        });
      });
    }
  };

  @action
  getResponse = (
    messageSentTimestamp: string,
    selectedOption: { id: number; value: string },
  ) => {
    if (
      (function onMoreCategorySelected() {
        return selectedOption.id === ResponseFromUser.VIEW_MORE;
      })()
    ) {
      this.makeOptionButtonsDisabled({
        messageSentTimestamp,
        targetOption: ResponseFromUser.VIEW_MORE,
      });
      this.generateAndAddMessage({
        messageBody: selectedOption.value,
        messageSender: this.rootStore.userStore.user.userId,
      });
      this.generateAndAddMessage({
        option: this.faq.inquiryCategories
          .map((e) => ({
            id: e.inquiryCategoryId,
            value: e.inquiryCategory,
          }))
          .slice(4),
      });
      this.scrollToLastMessage();
      return;
    }

    // ^^^^^^^^
    // if no more Category is selected, then the following lines are ignored (returned)

    this.messages.get(messageSentTimestamp)?.option &&
      this.makeOptionButtonsDisabled({
        messageSentTimestamp: messageSentTimestamp,
      });

    if (
      (function onGoToCategorySelection() {
        return selectedOption.id === ResponseFromUser.RESET_CATEGORY_SELECTION;
      })()
    ) {
      const latestMessageKey = Array.from(this.messages.keys())
        .map((key) => {
          return key;
        })
        .reduce((a, b) => (moment(a) > moment(b) ? a : b));

      this.makeOptionButtonsDisabled({
        messageSentTimestamp: latestMessageKey,
      });

      this.generateAndAddMessage({
        messageBody: selectedOption.value,
        messageSender: this.rootStore.userStore.user.userId,
      });
      this.goToCategorySelection();
    } else if (
      (function onCategorySelected(inputSelectionMap) {
        return (
          inputSelectionMap.inquiryCategory === undefined &&
          inputSelectionMap.inquirySubCategory === undefined
        );
      })(this._inputSelectionMap)
    ) {
      this._inputSelectionMap.inquiryCategory = selectedOption.id;

      this.generateAndAddMessage({
        messageBody: selectedOption.value,
        messageSender: this.rootStore.userStore.user.userId,
      });

      const fetchedFaqOption = this.faq.inquiryCategories
        .find((ee) => {
          return ee.inquiryCategoryId === selectedOption.id;
        })!
        .inquirySubCategories.map((e) => ({
          id: e.inquirySubCategoryId,
          value: e.inquirySubCategory,
        }));
      fetchedFaqOption.push({
        id: ResponseFromUser.FAQ_OPTION_SEND_MESSAGE,
        value: 'チャットで問い合わせる',
      });

      const lastMessageTimestamp = this.generateAndAddMessage({
        option: fetchedFaqOption,
      });

      const pastKeys = [];
      for (const key of this.messages.keys()) {
        if (key < lastMessageTimestamp) {
          pastKeys.push(key);
        }
      }
      pastKeys.forEach((e) => {
        this.messages.get(e)?.option?.forEach((e) => {
          e.isAlreadySelectedOption = true;
        });
      });
    } else if (
      (function onFaqSelected(inputSelectionMap) {
        return (
          inputSelectionMap.inquiryCategory !== undefined &&
          inputSelectionMap.inquirySubCategory === undefined
        );
      })(this._inputSelectionMap)
    ) {
      this._inputSelectionMap.inquirySubCategory = selectedOption.id;

      this.generateAndAddMessage({
        messageBody: selectedOption.value,
        messageSender: this.rootStore.userStore.user.userId,
      });

      if (
        this._inputSelectionMap.inquirySubCategory !==
        ResponseFromUser.FAQ_OPTION_SEND_MESSAGE
      ) {
        this.generateAndAddMessage({
          messageBody: this.faq.inquiryCategories
            .find((ee) => {
              return (
                ee.inquiryCategoryId === this._inputSelectionMap.inquiryCategory
              );
            })!
            .inquirySubCategories.find(
              (ee) => ee.inquirySubCategoryId === selectedOption.id,
            )!.answer,
        });

        this.generateAndAddMessage({
          messageBody: 'この情報は役に立ちましたか？',
          option: [
            { id: 1, value: 'はい' },
            { id: 2, value: 'いいえ（お問い合わせ内容を入力する）' },
          ],
        });
      } else {
        //直接問い合わせる
        this._inputSelectionMap.feedbackId = ResponseFromUser.NO;
        this.generateAndAddMessage({
          messageBody:
            'お問い合わせ内容を下のテキストボックスにご入力ください。\n宿泊施設にて確認の上ご案内いたしますまで、お時間いただくことご了承ください。\nお急ぎの場合は、詳細情報ページに記載の電話番号までお問い合わせください。',
        });
      }
    } else if (
      (function onFeedbackSelected(inputSelectionMap) {
        return (
          inputSelectionMap.inquiryCategory !== undefined &&
          inputSelectionMap.inquirySubCategory !== undefined &&
          inputSelectionMap.feedbackId === undefined
        );
      })(this._inputSelectionMap)
    ) {
      // この情報は役に立ちましたか？
      this._inputSelectionMap.feedbackId = selectedOption.id;
      this.generateAndAddMessage({
        messageBody: selectedOption.value,
        messageSender: this.rootStore.userStore.user.userId,
      });
      switch (this._inputSelectionMap.feedbackId) {
        case ResponseFromUser.YES:
          this.generateAndAddMessage({
            messageBody:
              'ありがとうございました。\n他に何かご質問はありますか。',
            option: [
              { id: 1, value: 'あります' },
              { id: 2, value: 'ありません' },
            ],
          });
          break;
        case ResponseFromUser.NO:
          this.generateAndAddMessage({
            messageBody:
              'お問い合わせ内容を下のテキストボックスにご入力ください。\n宿泊施設にて確認の上ご案内いたしますまで、お時間いただくことご了承ください。\nお急ぎの場合は、詳細情報ページに記載の電話番号までお問い合わせください。',
          });
          break;
      }
    } else if (
      (function onMoreFaq(inputSelectionMap) {
        return (
          inputSelectionMap.inquiryCategory !== undefined &&
          inputSelectionMap.inquirySubCategory !== undefined &&
          inputSelectionMap.feedbackId !== undefined
        );
      })(this._inputSelectionMap)
    ) {
      // 他に何か質問ある？
      this._inputSelectionMap.moreFaqId = selectedOption.id;

      this.generateAndAddMessage({
        messageBody: selectedOption.value,
        messageSender: this.rootStore.userStore.user.userId,
      });
      switch (this._inputSelectionMap.moreFaqId) {
        case ResponseFromUser.YES:
          this.goToCategorySelection();
          break;
        case ResponseFromUser.NO:
          this.generateAndAddMessage({
            messageBody:
              'また分からないことがありましたら、いつでもご質問ください。この度はありがとうございました。',
            linkTo: [
              {
                href: 'https://travel.rakuten.co.jp/',
                text: 'Rakuten Travel トップページ',
              },
            ],
          });
          break;
      }
    }

    this.scrollToLastMessage();
  };

  @action
  createFaqSummary = async (recipient: IRecipient, inquiryCategory: number) => {
    this.messages = observable.map(); // initialize messages

    this.faq = await FaqApi.getFaqs(
      recipient.serviceId,
      recipient.serviceAttribute,
      recipient.authId,
    );

    const category = this.getInquiryCategory(inquiryCategory);

    this.generateAndAddMessage({
      messageBody:
        'いつもありがとうございます。お問い合わせのカテゴリーを下記より選択してください。',
    });
    this.generateAndAddMessage({
      messageSender: this.rootStore.userStore.user.userId,
      messageBody: category,
    });
    this.generateAndAddMessage({
      messageBody:
        'お問い合わせ内容を下のテキストボックスにご入力ください。\n宿泊施設にて確認の上ご案内いたしますまで、お時間いただくことご了承ください。\nお急ぎの場合は、詳細情報ページに記載の電話番号までお問い合わせください。',
    });
  };

  @action
  getInquiryCategory = (inquiryCategory: number) => {
    return getInquiryCategoryName(inquiryCategory);
  };

  @computed
  get isFirstFaqMessageDisplayed(): boolean {
    return this.messages.size === 1;
  }

  @computed
  get isFaqUnresolved(): boolean {
    return (
      this._inputSelectionMap.inquiryCategory !== undefined &&
      this._inputSelectionMap.inquirySubCategory !== undefined &&
      this._inputSelectionMap.feedbackId === ResponseFromUser.NO
    );
  }

  @computed
  get isFaqResolvedAndNoMoreQuestion(): boolean {
    console.log(this._inputSelectionMap);
    return (
      this._inputSelectionMap.inquiryCategory !== undefined &&
      this._inputSelectionMap.inquirySubCategory !== undefined &&
      this._inputSelectionMap.feedbackId === ResponseFromUser.YES &&
      this._inputSelectionMap.moreFaqId === ResponseFromUser.NO
    );
  }

  @computed
  get isCategorySelected(): boolean {
    return this._inputSelectionMap.inquiryCategory !== undefined;
  }

  @computed
  get inputSelectionMap(): InputSelection {
    return this._inputSelectionMap;
  }

  @computed
  get sortedAutoMessages(): IAutoMessage[] {
    return Array.from(this.messages)
      .sort((a: [string, IAutoMessage], b: [string, IAutoMessage]): number => {
        return a[1].messageSentTimestamp < b[1].messageSentTimestamp ? -1 : 1;
      })
      .map((messageMapArray: [string, IAutoMessage]) => {
        return messageMapArray[1];
      });
  }

  initializeInputSelectionMap = () => {
    this._inputSelectionMap = {} as InputSelection;
  };

  scrollToLastMessage = () => {
    if (this.messages.size > 1) {
      setTimeout(() => {
        this.rootStore.inquiryPageStore.lastMessageDivScrollIntoView('smooth');
      }, 50);
    }
  };

  goToCategorySelection = () => {
    this.generateAndAddMessage({
      messageBody: 'お問い合わせのカテゴリーを下記より選択してください。',
      option: this.faq.inquiryCategories
        .map((e) => ({
          id: e.inquiryCategoryId,
          value: e.inquiryCategory,
        }))
        .slice(0, 4)
        .concat({
          id: ResponseFromUser.VIEW_MORE,
          value: 'もっと見る',
        }),
    });
    this.initializeInputSelectionMap();
  };

  generateAndAddMessage = (message: {}) => {
    const response = {
      ...{
        ...({} as IAutoMessage),
        messageSentTimestamp: moment()
          .add(this.messages.size, 'second')
          .utc()
          .format('YYYY-MM-DDTHH:mm:ssZZ'),
      },
      ...message,
    };
    this.messages.set(response.messageSentTimestamp, response);
    return response.messageSentTimestamp;
  };

  makeOptionButtonsDisabled = ({
    messageSentTimestamp,
    targetOption,
  }: {
    messageSentTimestamp: string;
    targetOption?: number;
  }) => {
    this.messages.get(messageSentTimestamp)!.option!.forEach((e) => {
      if (targetOption != null) {
        if (targetOption === e.id) {
          e.isAlreadySelectedOption = true;
        }
      } else {
        e.isAlreadySelectedOption = true;
      }
    });
  };

  constructor(rootStore: RootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
  }
}
